import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  getAccountIndicationPdf,
  type AccountPreapproval,
} from "@/lib/brokerAccounts";
import { bondRatePercent } from "@/lib/brokerBonds";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";

// BRK-S139 — the standing surety PROGRAM, on the account profile.
//
// A pre-approval is a program the INSURED holds, not a fact about one bond
// order: a single-bond limit, an aggregate program limit, and a rate. So it
// belongs on the account, and this is the surface a broker quotes from — which
// is why the figures are rendered the way Signal granted them and the letter is
// one click away.
//
// THREE STATES, and the two that are not "approved" are different facts:
//
//   • `approved`     — the three figures, the rate, when it was decided, and the
//     Program Indication letter.
//   • `not_granted`  — the insured has been evaluated and holds no program.
//   • `none`         — nobody has evaluated them. There is something to DO about
//     this one, where a refusal is an answer the broker already has, so it is
//     the only state that leads with the call to action.
//
// NOTHING HERE EXPLAINS A DECISION, and nothing here may. There is no score, no
// reason, no tier, no surety name, no A.M. Best rating and no Treasury status —
// not "not read", but not published, because Signal cannot state them to a
// broker (PMS-E015). `not_granted` collapses a refusal and a WITHDRAWAL
// indistinguishably, on purpose, and the word "declined" never appears:
// `not_granted` is not a decline. `test/lib/preapprovalDiscretion.test.ts` is
// what keeps that true of this repo rather than of this comment.

/** What to call the letter if PMS sends no `Content-Disposition` — a readable
 *  word, deliberately not the account id. */
const LETTER_FALLBACK = "program-indication.pdf";

/**
 * How each standing program reads (BRK-S161, consuming PMS-S435).
 *
 * ⚠️ **Signal Advance**, never "Advanced". The earlier drafts of these stories
 * said otherwise, and `test/lib/signalAdvanceNaming.test.ts` is what keeps the
 * correction true of the repo rather than of this comment.
 *
 * A program NAME is what Signal offers, which is why it may be said to a
 * broker at all; the rater that chose it stays underwriter-only and is never
 * published to this surface.
 */
const PROGRAM_LABEL: Record<"express" | "advance", string> = {
  express: "Signal Express program",
  advance: "Signal Advance program",
};

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words text-lg font-semibold text-brk-text">
        {value}
      </dd>
    </div>
  );
}

/**
 * BRK-S160 — the "Get pre-approved" call to action.
 *
 * Until this story the ONLY way to a program pre-qualification was starting a
 * bid bond and walking its whole intake, which is a lot of a broker's evening
 * for a question they wanted answered first. The standalone flow is a
 * subproduct of its own, so the CTA needs the SUBPRODUCT ID to link to — which
 * is the one thing this block cannot work out on its own.
 *
 * It arrives as a PROP, resolved by the page from the catalog by
 * `PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY`. Null/absent is an ordinary state and
 * not an error: a catalog with no such subproduct is a PMS that has not been
 * given the data yet (the deploy order is PMS → BRK → SUR → catalog data), and
 * the block falls back to today's "Start a bond request" so nothing is ever a
 * dead link.
 */
export function SuretyProgramBlock({
  accountId,
  preapproval,
  preapprovalSubproductId,
}: {
  accountId: string;
  preapproval: AccountPreapproval;
  /** The catalog subproduct that runs the standalone flow, when one exists. */
  preapprovalSubproductId?: string | null;
}) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function printLetter() {
    setBusy(true);
    setError(null);
    try {
      saveFile(
        await getAccountIndicationPdf(authedBlob, accountId),
        LETTER_FALLBACK,
      );
    } catch (err) {
      // A handled line, never a throw (NN #7). A 404 here means the mint has
      // not landed yet — best-effort upstream and retried — so the sentence PMS
      // sends is the one that fits.
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const terms = preapproval.status === "approved" ? preapproval.terms : null;

  return (
    <section
      aria-labelledby="surety-program"
      className="flex flex-col gap-5 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="surety-program" className="text-lg font-semibold text-brk-text">
          Surety program
        </h2>
        {preapproval.status === "approved" && (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            Approved
          </span>
        )}
      </div>

      {terms ? (
        <>
          {/* WHICH program, named (BRK-S161). Only for an approval — there is
              nothing to name beside a status that says the insured holds
              nothing. */}
          {preapproval.programType && (
            <p className="text-sm text-brk-dim">
              {PROGRAM_LABEL[preapproval.programType]}
            </p>
          )}
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Figure
              label="Single bond limit"
              value={formatPreapprovalMoney(terms.singleBondLimit)}
            />
            <Figure
              label="Aggregate program limit"
              value={formatPreapprovalMoney(terms.aggregateProgramLimit)}
            />
            {/* The rate is stored per $1,000 and rendered as a PERCENT —
                `ratePer1000 / 10`, the one conversion (`bondRatePercent`).
                Rendering the stored figure directly would overstate the rate by
                10×. "Flat rate" is Signal's own wording for a program rate that
                does not tier with the bond amount. */}
            <Figure
              label="Bond rate"
              value={`${bondRatePercent(terms.ratePer1000)}% — Flat rate`}
            />
          </dl>

          <div className="flex flex-col gap-2">
            {preapproval.evaluatedAt && (
              <p className="text-sm text-brk-dim">
                Approved {formatBondDate(preapproval.evaluatedAt)}
              </p>
            )}
            {/* Offered only when the LETTER exists — its own axis, not derivable
                from the status (see `getAccountIndicationPdf`). */}
            {preapproval.indicationAvailable ? (
              <button
                type="button"
                onClick={printLetter}
                disabled={busy}
                className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Preparing…" : "Print indication letter"}
              </button>
            ) : (
              <p className="text-sm text-brk-dim">
                Your indication letter is still being prepared — it will appear
                here shortly.
              </p>
            )}
            {/* THE SIGNAL ADVANCE INVITATION (BRK-S161), and only for an
                insured on Signal Express — there is nowhere for an insured
                already on Advance to go, and offering it would be an upgrade
                to the program they hold.

                It is a LINK into the insured's own bond orders rather than a
                button: enrolment is answered per ORDER (PMS-S435's route takes
                a submission id), and this block has no order to address. That
                is the honest shape of it — "here is where to do this" — rather
                than a control that would have to pick an order on the broker's
                behalf. */}
            {preapproval.programType === "express" && (
              <Link
                to={`/surety/submissions?accountId=${encodeURIComponent(accountId)}`}
                className="self-start text-sm font-medium text-brk-accent hover:underline"
              >
                Opt in to Signal Advance →
              </Link>
            )}
            {error && (
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-start gap-3">
          {/* NEUTRAL, in both non-approved states. `not_granted` is not a
              decline and must not read as one, and it covers a refusal AND a
              withdrawal indistinguishably. */}
          <p className="text-sm text-brk-text">
            No program pre-qualification on file for this insured.
          </p>
          <p className="text-sm text-brk-dim">
            {preapprovalSubproductId
              ? preapproval.status === "none"
                ? "Get pre-approved first and you’ll know this insured’s limits before you quote."
                : "Ask us to take another look — the figures move."
              : preapproval.status === "none"
                ? "Start a bond request and we’ll review the program as part of it."
                : "Start a bond request and we’ll take another look — the figures move."}
          </p>
          {/* Mobile first: the two actions stack full-width at 375px and sit
              side by side from `sm:` up. */}
          <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
            {preapprovalSubproductId && (
              <Link
                to={`/surety/submissions/new?subproduct=${encodeURIComponent(
                  preapprovalSubproductId,
                )}&account=${encodeURIComponent(accountId)}`}
                className="rounded-md bg-brk-accent px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
              >
                Get pre-approved
              </Link>
            )}
            {/* Kept, and SECONDARY once the standalone flow exists: a bond
                request still reviews the program as part of itself, so a broker
                who came here to start one is not sent the long way round. */}
            <Link
              to="/surety/construction"
              className={
                preapprovalSubproductId
                  ? "text-center text-sm font-medium text-brk-accent hover:underline"
                  : "rounded-md bg-brk-accent px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
              }
            >
              Start a bond request
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
